import { Component, Input } from '@angular/core';
import { NavController, PopoverController } from 'ionic-angular';
import { ProgressionSource } from '../../../../../model/progression-source';
import { Progression } from '../../../../../model/progression';
import { Rodeo } from '../../../../../model/rodeo';
import { ProgressionSourceModal } from './progression-source';
import { RodeoEventSchedule } from 'model/rodeoEventSchedule';
import { AlertMessager } from '../../../../../lib/alertMessager';
import { ProgressionTieBreaker } from 'model/progression-tiebreaker';
import { ProgressionTieBreakerModal } from './progression-tiebreaker';
import { PayoutSource } from 'model/payout-source';
import { ProgressionReport } from './progression-report';
import { PayoutConfig } from 'model/payoutConfig';

@Component({
    selector: 'staff-progressions',
    templateUrl: 'progressions.html'
})
export class ProgressionsSection {
    @Input() set rodeo(rodeo: Rodeo) {
        this._rodeo = rodeo;

        rodeo.progressions.forEach((progression) => {
            progression.progressionSources.sort((a, b) => {
                if (a.priorityOrder < b.priorityOrder)
                    return -1;
                if (a.priorityOrder > b.priorityOrder)
                    return 1;

                return 0;
            });

            progression.tieBreakers.sort((a, b) => {
                if (a.priorityOrder < b.priorityOrder)
                    return -1;
                if (a.priorityOrder > b.priorityOrder)
                    return 1;

                return 0;
            });
        })

        this.filterResults();
    }

    get rodeo() {
        return this._rodeo;
    }

    @Input() progressionOrderFormats: string[] = [];

    _rodeo: Rodeo;
    toRounds: any[] = [];
    popoverActive: boolean;

    filterToSchedule: any = 'All';

    constructor(private popoverController: PopoverController, private alert: AlertMessager, private nav: NavController) {
        this.filterResults();
    }

    matchStrings(a, b) {
        return a == b;
    }

    filterResults() {
        if (!this.rodeo)
            return;

        this.rodeo.progressions.forEach((progression) => {
            (progression as any).show = true;
            this.updateToRounds(progression);
        });

        if (this.filterToSchedule != 'All' && this.rodeo.progressions) {
            this.rodeo.progressions.forEach((progression) => {
                if (progression.toRodeoEventScheduleId != this.filterToSchedule.id) {
                    (progression as any).show = false;
                }
            })
        }
    }

    updateToRounds(progression: Progression) {
        let progressionIndex = this.rodeo.progressions.indexOf(progression);
        const rodeoEvent = this.rodeo.rodeoEvents.filter(x => x.schedules.some(y => y.id == progression.toRodeoEventScheduleId))[0];

        if (!rodeoEvent)
            return;

        this.toRounds[progressionIndex] = [];

        for (let i = 0; i < rodeoEvent.rounds + (rodeoEvent.shortGo ? 1 : 0); i++) {
            this.toRounds[progressionIndex].push(i + 1);
        }

        if (!progression.toRound || this.toRounds[progressionIndex].indexOf(progression.toRound) == -1)
            progression.toRound = this.toRounds[progressionIndex][0];
    }

    addNewProgression() {
        if (!this.rodeo.progressions)
            this.rodeo.progressions = [];

        var newProgression = new Progression();
        if (this.filterToSchedule != 'All') {
            newProgression.toRodeoEventScheduleId = this.filterToSchedule.id;
            this.updateToRounds(newProgression);
        }

        this.rodeo.progressions.push(newProgression);

        this.filterResults();
    }

    addNewProgressionSource(progression: Progression) {
        if (!progression.progressionSources)
            progression.progressionSources = [];

        this.editSource(progression, null);
    }

    editSource(progression: Progression, source: ProgressionSource) {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        let popover = this.popoverController.create(ProgressionSourceModal, {
            organization: this.rodeo.organization,
            rodeo: this.rodeo,
            progressionSource: source
        }, {
            cssClass: 'progression-source'
        });

        popover.present();

        let sourceIndex = progression.progressionSources.indexOf(source);
        popover.onDidDismiss((data) => {
            this.popoverActive = false;

            if (data == 0)
                progression.progressionSources = progression.progressionSources.filter(x => x != source);
            else if (data) {
                if (source) {
                    progression.progressionSources[sourceIndex] = data as ProgressionSource;
                } else
                    progression.progressionSources.push(data as ProgressionSource);

                progression.progressionSources.sort((a, b) => {
                    if (a.priorityOrder < b.priorityOrder)
                        return -1;
                    if (a.priorityOrder > b.priorityOrder)
                        return 1;

                    return 0;
                });
            }
        })
    }

    addNewTieBreaker(progression: Progression) {
        if (!progression.tieBreakers)
            progression.tieBreakers = [];

        this.editTieBreaker(progression, null);
    }

    editTieBreaker(progression: Progression, tieBreaker: ProgressionTieBreaker) {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        let popover = this.popoverController.create(ProgressionTieBreakerModal, {
            organization: this.rodeo.organization,
            rodeo: this.rodeo,
            tieBreaker: tieBreaker
        }, {
            cssClass: 'progression-tiebreaker'
        });

        popover.present();

        popover.onDidDismiss((data) => {
            this.popoverActive = false;

            if (data == 0)
                progression.tieBreakers = progression.tieBreakers.filter(x => x != tieBreaker);
            else if (data) {
                var originalIndex = progression.tieBreakers.indexOf(tieBreaker);

                if (originalIndex != -1) {
                    progression.tieBreakers[originalIndex] = data as ProgressionTieBreaker;
                } else
                    progression.tieBreakers.push(data as ProgressionTieBreaker);

                progression.tieBreakers.sort((a, b) => {
                    if (a.priorityOrder < b.priorityOrder)
                        return -1;
                    if (a.priorityOrder > b.priorityOrder)
                        return 1;

                    return 0;
                });
            }
        })
    }

    getPayoutConfigName(payoutConfigId) {
        const payoutConfig = this.rodeo.payoutConfigs.filter(x => x.id == payoutConfigId)[0];

        if (payoutConfig)
            return payoutConfig.name;
        else
            return '';
    }

    printProgression(progression: Progression) {
        if (this.popoverActive)
            return;

        this.popoverActive = true;

        this.nav.push(ProgressionReport, {
            rodeo: this.rodeo,
            print: true,
            progression: progression
        }).then(() => { this.popoverActive = false; });
    }

    getTieBreakerBreakByMessage(tieBreaker: ProgressionTieBreaker) {
        if (tieBreaker.breakByTimeScore)
            return 'Time/Score';
        else if (tieBreaker.breakByTotalTimeScore)
            return 'Total Time/Score';
        else if (tieBreaker.breakByPointSeriesPlace)
            return 'Points Place';
        else if (tieBreaker.breakByAthleteScore)
            return 'Single Rider Score';
        else if (tieBreaker.breakByCombinedAthleteScore)
            return 'Total Rider Score';
        else if (tieBreaker.breakByRideTime)
            return 'Ride Time';
    }

    getSourceName(source: ProgressionSource) {
        var matchingSources = this.rodeo.payoutConfigs.filter(x => x.id == source.payoutConfigId);

        if (!matchingSources)
            return 'SOURCE PATH ERROR';
        else 
            return matchingSources[0].name;
    }
}


// WEBPACK FOOTER //
// ./src/pages/staff/rodeo/settings/progressions/progressions.ts